'use client';

import { useMutation } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { ComponentProps, ReactNode, createContext, useContext } from 'react';
import { UseFormReturn, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { QueryApi } from '@/features/query/api';
import { EventService } from '@/shared/lib/event-service';
import { Alert } from '@/shared/ui/alert';
import { Button } from '@/shared/ui/button';
import { Form } from '@/shared/ui/form';
import { Layout } from '@/shared/ui/layout';
import { Modal } from '@/shared/ui/modal';
import { ModalService } from '@/shared/ui/modal/ModalService';
import { ModalProps } from '@/shared/ui/modal/types';
import { OrderMageBranding } from '@/shared/ui/ordermage-branding';
import './auth-modal.scss';

type FormValues = {
    password: string;
};

type Props = ModalProps & {
    token: string;
    children?: ReactNode;
};

type SetNewPasswordModalContextValue = {
    form: UseFormReturn<FormValues>;
    token: string;
    onClose: ModalProps['onClose'];
};

const SetNewPasswordModalContext = createContext<SetNewPasswordModalContextValue | null>(null);

const useSetNewPasswordModalContext = () => {
    const context = useContext(SetNewPasswordModalContext);
    if (!context) {
        throw new Error('SetNewPasswordModal components must be used within SetNewPasswordModal.');
    }
    return context;
};

const SetNewPasswordModalProvider = ({ children, token, ...props }: Props) => {
    const form = useForm<FormValues>({
        defaultValues: {
            password: '',
        },
    });

    return (
        <SetNewPasswordModalContext.Provider
            value={{
                form,
                token,
                onClose: props.onClose,
            }}
        >
            {children}
        </SetNewPasswordModalContext.Provider>
    );
};

/** Renders only the title text. Use inside prefab: "Modal.Title". */
const SetNewPasswordModalTitle = () => {
    const t = useTranslations();
    return <>{t('SET_NEW_PASSWORD_MODAL.TITLE')}</>;
};

type FormProps = ComponentProps<typeof Layout>;
const SetNewPasswordModalForm = ({ children, media, ...props }: FormProps) => {
    const t = useTranslations();
    const { form, token, onClose } = useSetNewPasswordModalContext();
    const { mutateAsync } = useMutation(QueryApi.Customer.mutations.ResetPassword);

    const handleSubmit = async (data: FormValues) => {
        const res = await mutateAsync({ token, password: data.password });
        if (res.result === 'OK') {
            ModalService.showPrefab('LoginModal', {}, { position: 'replace' });
            toast(({ closeToast }) => (
                <Alert
                    heading={t('SET_NEW_PASSWORD_MODAL.NOTIFICATIONS.SUCCESS')}
                    type="info"
                    onClose={closeToast}
                />
            ));
            onClose();
            return;
        }
        toast(({ closeToast }) => (
            <Alert
                heading={t('SET_NEW_PASSWORD_MODAL.NOTIFICATIONS.ERROR')}
                type="error"
                onClose={closeToast}
            />
        ));
    };

    return (
        <Layout
            element={Form}
            {...form}
            media={media}
            {...props}
            onSubmit={form.handleSubmit(handleSubmit)}
            className="auth-modal__form"
        >
            {children}
        </Layout>
    );
};

const SetNewPasswordModalPasswordField = () => {
    const t = useTranslations();
    return (
        <Form.PasswordField
            data-testid="set-new-password-modal-password-field"
            name="password"
            label={t('SET_NEW_PASSWORD_MODAL.NEW_PASSWORD_LABEL')}
            rules={{
                required: true,
            }}
            isCheckerEnabled
        />
    );
};

const SetNewPasswordModalSubmitButton = () => {
    const t = useTranslations();
    return (
        <Button
            type="submit"
            data-testid="set-new-password-modal-submit-button"
            className="auth-modal__button"
        >
            {t('SET_NEW_PASSWORD_MODAL.SUBMIT_BUTTON')}
        </Button>
    );
};

const SetNewPasswordModalRememberPasswordText = () => {
    const t = useTranslations();
    const handleShowLogin = () => {
        ModalService.showPrefab('LoginModal', {}, { position: 'replace' });
    };
    return (
        <div className="auth-modal__register-text">
            {t.rich('SET_NEW_PASSWORD_MODAL.REMEMBER_PASSWORD', {
                button: (chunks) => (
                    <button
                        type="button"
                        onClick={handleShowLogin}
                        className="auth-modal__checkbox-btn"
                        data-testid="set-new-password-modal-login-button"
                    >
                        {chunks}
                    </button>
                ),
            })}
        </div>
    );
};

const SetNewPasswordModalBottomPanel = () => {
    const handleShowTerms = () => {
        EventService.trigger('register-terms-and-conditions:view-detail', {});
    };
    return <OrderMageBranding onLogoClick={handleShowTerms} />;
};

export const SetNewPasswordModalWrapper = ({ children, ...props }: Props) => {
    return (
        <SetNewPasswordModalProvider {...props}>
            <Modal
                {...props}
                wrapperProps={{
                    className: 'auth-modal',
                    containerProps: {
                        className: 'auth-modal__container',
                    },
                    'data-testid': 'set-new-password-modal',
                }}
            >
                {children}
            </Modal>
        </SetNewPasswordModalProvider>
    );
};

/** Provider that only provides context and renders children (Modal prefab). */
export const SetNewPasswordModalProviderComponent = ({ children, ...props }: Props) => (
    <SetNewPasswordModalProvider {...props}>{children}</SetNewPasswordModalProvider>
);

export {
    SetNewPasswordModalWrapper as Wrapper,
    SetNewPasswordModalProviderComponent as Provider,
    SetNewPasswordModalTitle as Title,
    SetNewPasswordModalForm as Form,
    SetNewPasswordModalPasswordField as PasswordField,
    SetNewPasswordModalSubmitButton as SubmitButton,
    SetNewPasswordModalRememberPasswordText as RememberPasswordText,
    SetNewPasswordModalBottomPanel as BottomPanel,
};

export { SetNewPasswordModalWrapper as SetNewPasswordModal };
